import { useEffect, useRef, useState } from "react";

import "owl.carousel";
import type { ListingImage } from "@gql";

interface Props {
  activateKeyboardShortcuts?: boolean;
  responsive?: { [breakpoint: string]: OwlCarousel.Options } | undefined;
  images: ListingImage[] | undefined;
  selectedIndex?: number;
  onImageClicked?: (selectedIndex: number) => void;
}

/**
 * Renders an image carousel with the passed images
 *
 * @prop images - (Request) contains the images to display
 * @prop selectedImage - (Optional) the image to display initialy
 * @prop responsive - (Optional) responsive options for Owl Carousel
 */
const ImageCarousel = ({
  activateKeyboardShortcuts,
  responsive,
  images,
  selectedIndex,
  onImageClicked,
}: Props) => {
  const owlCarousel = useRef<HTMLDivElement>(null);
  const [mouseOver, setMouseOver] = useState(false);

  useEffect(() => {
    initializeCarousel();
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const initializeCarousel = () => {
    if (owlCarousel.current) {
      const element = $(owlCarousel?.current);

      if (element.length !== 0) {
        element.owlCarousel({
          center: true,
          loop: true,
          nav: true,
          autoWidth: true,
          lazyLoad: true,
          startPosition: selectedIndex || 0,
          margin: 20,
          navText: ["<div id='icon-left'></div>", "<div id='icon-right'></div>"],
          responsive,
        });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (owlCarousel.current) {
      const element = $(owlCarousel.current);

      if (mouseOver || activateKeyboardShortcuts) {
        if (e.key === "ArrowRight") {
          element.trigger("next.owl.carousel");
        } else if (e.key === "ArrowLeft") {
          element.trigger("prev.owl.carousel");
        }
      }
    }
  };

  const handleImageClicked = (index: number) => {
    onImageClicked?.(index);
  };

  if (!images || images.length === 0) return null;

  return (
    <div
      className="image-carousel-component"
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <div className="owl-carousel" ref={owlCarousel}>
        {images.map(({ id, url }, i) => {
          return (
            <div key={id} className="item" onClick={() => handleImageClicked(i)}>
              <img src={url} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

ImageCarousel.defaultProps = {
  responsive: {
    0: {
      items: 1,
      autoWidth: false,
    },
    600: {
      items: 3,
    },
    1000: {
      items: 3,
    },
  },
};

export { ImageCarousel };
